import { FC, ReactNode } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { tokens } from '../../theme';
import InfoTip from '../InfoTip';
import { KPI, KpiDefinition, KpiKey, formatKpi } from '../../helpers/webinarKpiDefinitions';

interface KpiTileBasis {
  /** Kleine Zeile unter dem Namen, z. B. „140 von 375 rechtzeitig Angemeldeten". */
  sub?: ReactNode;
  icon?: ReactNode;
  /** Ersetzt den Namen aus der Definition, wenn die Kachel eine Variante zeigt. */
  labelOverride?: string;
  /** Ersetzt die Bedeutung im Tooltip, wenn die Variante anders zählt (z. B. Sektions-Sicht: nur Live-Anmeldungen). */
  tooltipOverride?: string;
}

/**
 * Opt-in (P1-V3 § 5, Stufe 0 Plan 2026-10-23-p1-p2-kpi-review-umbau): freie Kachel ohne `KPI`-Eintrag — Label, fertig
 * formatierter Wert und Tooltip kommen vom Aufrufer (Begriffe aus `TERMS`), z. B. „Teilnehmer im Raum", „mindestens 165".
 * Ohne `frei` verhält sich die Kachel wie vorher (geteilt mit /webinar-statistics).
 */
export interface KpiTileFrei {
  label: string;
  wert: ReactNode;
  tip?: string;
  /** Farbe der Zahl; Standard wie eine KPI ohne eigene Farbe. */
  farbe?: string;
}

type KpiTileProps = KpiTileBasis &
  ({ metric: KpiKey; value: number | null | undefined; frei?: undefined } | { frei: KpiTileFrei; metric?: undefined; value?: undefined });

/**
 * Kennzahl-Kachel. Name, Tooltip und Format kommen aus webinarKpiDefinitions.ts — eine Kachel
 * kann damit nichts anderes behaupten als die gleichnamige Tabellenspalte.
 */
const KpiTile: FC<KpiTileProps> = ({ metric, value, frei, sub, icon, labelOverride, tooltipOverride }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const def: KpiDefinition | null = frei ? null : KPI[metric as KpiKey];
  const zahl = frei ? frei.wert : formatKpi(metric as KpiKey, value);
  const farbe = frei ? frei.farbe : def?.color;
  const label = labelOverride ?? (frei ? frei.label : def?.label);
  const tip = tooltipOverride ?? (frei ? frei.tip : def ? `${def.tooltip}\n\nFormel: ${def.formel}` : undefined);
  return (
    <Box
      sx={{
        backgroundColor: colors.primary[400],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        p: '16px',
        minHeight: 96,
        // Grid-Kind darf unter seine Inhaltsbreite schrumpfen, sonst laeuft die Kachel auf
        // schmalen Fenstern rechts aus der Seite (gemessen bei 500 px).
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        {icon && <Box sx={{ display: { xs: 'none', md: 'flex' } }}>{icon}</Box>}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ color: farbe ?? colors.grey[100], whiteSpace: 'nowrap' }}
          >
            {zahl}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color={colors.greenAccent[500]}>{label}</Typography>
            {tip && <InfoTip text={tip} />}
          </Box>
          {sub && (
            <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block' }}>
              {sub}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default KpiTile;
